import React from 'react';
import { useForm } from 'react-hook-form';
import { useQueryClient } from '@tanstack/react-query';

import { TrackableList, CreateTrackableItem } from 'types/trackables';

import { useCreateTrackableItem } from '../hooks/useCreateTrackableItem';
import { useUploadTrackableFile } from '../hooks/useUploadTrackableFile';

import { Drawer, DrawerProps } from 'components/Drawer/DrawerDeprecated';
import { Button } from 'components/Button/Button';
import { ButtonGroup } from 'components/ButtonGroup/ButtonGroup';
import { Field } from 'components/Field/Field';
import { Textarea } from 'components/Textarea/Textarea';
import { Text } from 'components/Text/Text';

export type CSVImportTrackableItemsDialogProps = DrawerProps & {
  trackableList: TrackableList;
  onSuccess?: () => void;
};

interface FormData {
  csvData: string;
}

export function CSVImportTrackableItemsDialog({
  trackableList,
  open,
  onClose,
  onSuccess,
}: CSVImportTrackableItemsDialogProps) {
  const queryClient = useQueryClient();
  const createTrackableItem = useCreateTrackableItem();
  const { uploadTrackableFile } = useUploadTrackableFile();

  const [isProcessing, setIsProcessing] = React.useState(false);
  const [processStatus, setProcessStatus] = React.useState<string>('');
  const [error, setError] = React.useState<string>('');

  const isFaceList = trackableList.object_type === 'face';
  const isVehicleList = trackableList.object_type === 'vehicle';
  const isCustomList = trackableList.object_type === 'custom';

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    watch,
  } = useForm<FormData>({
    defaultValues: {
      csvData: '',
    },
  });

  const downloadImageAsFile = async (
    url: string,
    filename: string
  ): Promise<File | null> => {
    try {
      const response = await fetch(url);
      if (!response.ok) {
        console.error(
          `Failed to download image from ${url}: ${response.statusText}`
        );
        return null;
      }

      const blob = await response.blob();

      // Create a File object from the blob
      const file = new File([blob], filename, {
        type: blob.type || 'image/jpeg',
      });
      return file;
    } catch (error) {
      console.error(`Failed to download image from ${url}:`, error);
      return null;
    }
  };

  const parseCSVLine = (line: string): string[] => {
    const result = [];
    let current = '';
    let inQuotes = false;

    for (let i = 0; i < line.length; i++) {
      const char = line[i];

      if (char === '"') {
        inQuotes = !inQuotes;
      } else if (char === ',' && !inQuotes) {
        // Trim whitespace from each field
        result.push(current.trim());
        current = '';
      } else {
        current += char;
      }
    }

    if (current) {
      // Trim whitespace from the last field
      result.push(current.trim());
    }

    // Ensure all fields are trimmed (double-check)
    return result.map(field => field.trim());
  };

  const onSubmit = async (data: FormData) => {
    if (!data.csvData) return;

    setIsProcessing(true);
    setProcessStatus('Processing CSV data...');
    setError('');

    try {
      const lines = data.csvData.split('\n').filter((line) => line.trim());

      if (lines.length === 0) {
        setError('No data found in CSV');
        return;
      }

      // Parse header - trim and lowercase all header fields
      const header = parseCSVLine(lines[0]).map(h => h.toLowerCase().trim());

      // Validate required columns
      const nameIndex = header.indexOf('name');
      if (nameIndex === -1) {
        setError('CSV must have a "name" column');
        return;
      }

      const descriptionIndex = header.indexOf('description');
      const labelsIndex = header.indexOf('labels');

      // Type-specific columns
      const faceUrlIndex = header.indexOf('face_url');
      const licensePlateIndex = header.indexOf('license_plate');

      // For custom lists, find all attribute columns (not name, description, labels)
      const attributeIndices: { index: number; key: string }[] = [];
      if (isCustomList) {
        header.forEach((col, index) => {
          const trimmedCol = col.trim();
          if (!['name', 'description', 'labels'].includes(trimmedCol)) {
            attributeIndices.push({ index, key: trimmedCol });
          }
        });
      }

      // Process data rows
      let created = 0;
      const totalRows = lines.length - 1;

      for (let i = 1; i < lines.length; i++) {
        const row = parseCSVLine(lines[i]);

        if (!row[nameIndex]) {
          console.warn(`Skipping row ${i}: no name provided`);
          continue;
        }

        setProcessStatus(
          `Creating item ${created + 1} of ${totalRows}: ${row[nameIndex].trim()}`
        );

        const labels = row[labelsIndex]
          ? row[labelsIndex]
              .split(';')
              .map((l) => l.trim())
              .filter(Boolean)
          : [];

        const attributes: Record<string, any> = {};

        if (isVehicleList && licensePlateIndex !== -1 && row[licensePlateIndex]) {
          attributes.licensePlate = row[licensePlateIndex].trim();
        } else if (isCustomList) {
          attributeIndices.forEach(({ index, key }) => {
            if (row[index]) {
              attributes[key] = row[index].trim();
            }
          });
        }

        const newItem: CreateTrackableItem = {
          name: row[nameIndex].trim(),
          description: row[descriptionIndex]?.trim() || undefined,
          labels,
          attributes:
            Object.keys(attributes).length > 0 ? attributes : undefined,
        };

        const createdItem = await createTrackableItem.mutateAsync({
          trackable_list_id: trackableList.id,
          ...newItem,
        });

        // For face lists with face_url, download and upload the image
        if (
          isFaceList &&
          faceUrlIndex !== -1 &&
          row[faceUrlIndex] &&
          createdItem
        ) {
          setProcessStatus(`Downloading image for ${row[nameIndex].trim()}...`);

          const imageUrl = row[faceUrlIndex].trim();
          const filename = `${row[nameIndex].trim()}.jpg`; // Default to .jpg extension
          const imageFile = await downloadImageAsFile(imageUrl, filename);

          if (imageFile) {
            setProcessStatus(`Uploading image for ${row[nameIndex].trim()}...`);
            try {
              await uploadTrackableFile(imageFile, {
                trackable_list_id: trackableList.id,
                trackable_item_id: createdItem.id,
                name: filename,
                size: imageFile.size,
              });
            } catch (uploadError) {
              console.error(
                `Failed to upload image for ${row[nameIndex].trim()}:`,
                uploadError
              );
            }
          }
        }

        created++;
      }

      queryClient.invalidateQueries(['trackable-items']);
      queryClient.invalidateQueries(['trackable-files']);

      reset();
      onSuccess?.();
      onClose?.();
    } catch (error) {
      console.error('CSV import failed:', error);
      setError('Error occurred during CSV import: ' + (error as Error).message);
      setProcessStatus('');
    } finally {
      setIsProcessing(false);
      if (!error) {
        setProcessStatus('');
      }
    }
  };

  const csvData = watch('csvData');

  // Generate example CSV based on list type
  const exampleCSV = React.useMemo(() => {
    if (isFaceList) {
      return `name,description,labels,face_url
John Doe,Employee,employee;security,https://example.com/john.jpg
Jane Smith,Manager,employee;manager,https://example.com/jane.jpg`;
    } else if (isVehicleList) {
      return `name,description,labels,license_plate
Company Van,Delivery vehicle,company;delivery,ABC123
Employee Car,Personal vehicle,employee,XYZ789`;
    } else {
      return `name,description,labels,color,size,priority
Item 1,First item,important;red,red,large,high
Item 2,Second item,blue,blue,medium,normal`;
    }
  }, [isFaceList, isVehicleList]);

  const downloadTemplate = () => {
    // Create template CSV with more descriptive placeholder values
    let templateCSV = '';
    let fileName = '';

    if (isFaceList) {
      templateCSV = `name,description,labels,face_url
John Doe,Employee in Engineering Department,employee;security;engineering,https://example.com/faces/john-doe.jpg
Jane Smith,Manager in Sales Department,employee;manager;sales,https://example.com/faces/jane-smith.jpg
Bob Johnson,Visitor from Company XYZ,visitor;temporary,https://example.com/faces/bob-johnson.jpg`;
      fileName = 'face-list-template.csv';
    } else if (isVehicleList) {
      templateCSV = `name,description,labels,license_plate
Company Van #1,White Ford Transit - Delivery vehicle,company;delivery;van,ABC-1234
Employee Car - John,Blue Toyota Camry - Personal vehicle,employee;personal,XYZ-5678
Visitor Vehicle,Red Honda Civic - Temporary visitor,visitor;temporary,TEMP-999`;
      fileName = 'vehicle-list-template.csv';
    } else {
      // For custom lists, include some generic attribute columns as examples
      templateCSV = `name,description,labels,category,priority,status,location
Item Alpha,First item with high priority,important;urgent,Category A,high,active,Building 1
Item Beta,Second item with medium priority,regular;tracked,Category B,medium,pending,Building 2
Item Gamma,Third item with low priority,optional,Category C,low,inactive,Building 3`;
      fileName = 'custom-list-template.csv';
    }

    // Create a Blob from the CSV string
    const blob = new Blob([templateCSV], { type: 'text/csv;charset=utf-8;' });

    // Create a temporary URL for the blob
    const url = URL.createObjectURL(blob);

    // Create a temporary anchor element and trigger download
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', fileName);
    link.style.display = 'none';

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    // Clean up the URL
    URL.revokeObjectURL(url);
  };

  return (
    <Drawer title="CSV Import" open={open} onClose={onClose}>
      <Text type="paragraph">
        Import multiple items from CSV data into the "{trackableList.name}"
        trackable list.
      </Text>

      <div className="mt-4 space-y-2 rounded-lg bg-gray-100 p-3 dark:bg-gray-800">
        <div className="flex items-center justify-between">
          <p className="text-sm font-medium">CSV Format:</p>
          <Button
            type="button"
            variant="secondary"
            size="small"
            onClick={downloadTemplate}
          >
            Download Template
          </Button>
        </div>
        <ul className="space-y-1 text-xs text-gray-600 dark:text-gray-400">
          <li>
            • Required columns: <code>name</code>
          </li>
          <li>
            • Optional columns: <code>description</code>, <code>labels</code>{' '}
            (semicolon-separated)
          </li>
          {isFaceList && (
            <li>
              • Face-specific: <code>face_url</code> (images will be downloaded
              and uploaded)
            </li>
          )}
          {isVehicleList && (
            <li>
              • Vehicle-specific: <code>license_plate</code>
            </li>
          )}
          {isCustomList && (
            <li>
              • Custom attributes: Any additional columns become attributes
            </li>
          )}
        </ul>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="mt-6">
        <div className="space-y-4">
          <Field
            label="CSV Data"
            required
            error={errors.csvData?.message || error}
            info="Paste your CSV data below. First row should be column headers. You can download a template using the button above."
          >
            <Textarea
              {...register('csvData', {
                required: 'Please enter CSV data.',
              })}
              placeholder={exampleCSV}
              rows={12}
              className="font-mono text-xs"
            />
          </Field>

          {csvData && (
            <div className="text-sm text-gray-600 dark:text-gray-400">
              Preview: {csvData.split('\n').filter((l) => l.trim()).length - 1}{' '}
              data rows detected
            </div>
          )}

          {/* Progress status */}
          {isProcessing && (
            <div className="space-y-2">
              <p className="text-sm font-medium text-neutral-700 dark:text-neutral-200">
                {processStatus}
              </p>
            </div>
          )}
        </div>

        <div style={{ marginTop: '1.5rem' }}>
          <ButtonGroup>
            <Button
              type="button"
              variant="secondary"
              size="small"
              onClick={onClose}
              disabled={isProcessing}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="primary"
              size="small"
              loading={isProcessing}
              disabled={!csvData}
            >
              Import
            </Button>
          </ButtonGroup>
        </div>
      </form>
    </Drawer>
  );
}
